import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { AuthProvider, SIGNED_OUT_PATH, useAuth } from './auth/AuthProvider.tsx';
import { AppShell } from './components/shell/AppShell.tsx';
import { IdleSessionGuard } from './components/IdleSessionGuard.tsx';
import { RenewalDialog } from './components/RenewalDialog.tsx';
import { MoneyProvider } from './money.tsx';
import { ActivityPage } from './pages/ActivityPage.tsx';
import { CataloguePage } from './pages/CataloguePage.tsx';
import { CustomisationsPage } from './pages/CustomisationsPage.tsx';
import { AppsAndRoutinesPage } from './pages/AppsAndRoutinesPage.tsx';
import { WorkflowPage, WorkflowRunPage } from './pages/WorkflowPages.tsx';
import { FoundryPage } from './pages/FoundryPage.tsx';
import { ComputePage } from './pages/ComputePage.tsx';
import { DesignSystemsPage } from './pages/DesignSystemsPage.tsx';
import { ToolsPage } from './pages/ToolsPage.tsx';
import { SecretsPage } from './pages/SecretsPage.tsx';
import { EnrolPage } from './pages/EnrolPage.tsx';
import { ProjectPage } from './pages/ProjectPage.tsx';
import { TeamworkSessionPage } from './components/teamwork/TeamworkSession.tsx';
import { InboxPage } from './pages/InboxPage.tsx';
import { LivePage } from './pages/LivePage.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { MemoryPage } from './pages/MemoryPage.tsx';
import { LabellingPage } from './pages/LabellingPage.tsx';
import { AuditPage } from './pages/AuditPage.tsx';
import { BackupsPage } from './pages/BackupsPage.tsx';
import { UpdatesPage } from './pages/UpdatesPage.tsx';
import { DownloadsPage } from './pages/DownloadsPage.tsx';
import { BillingPage } from './pages/BillingPage.tsx';
import { QuotasPage } from './pages/QuotasPage.tsx';
import { BudgetsPage } from './pages/BudgetsPage.tsx';
import { CostsPage } from './pages/CostsPage.tsx';
import { PortfolioPage } from './pages/PortfolioPage.tsx';
import { ProjectsPage } from './pages/ProjectsPage.tsx';
import { TeamsPage } from './pages/TeamsPage.tsx';
import { NodesPage } from './pages/NodesPage.tsx';
import { MeshPage } from './pages/MeshPage.tsx';
import { PoliciesPage } from './pages/PoliciesPage.tsx';
import { AssurancePage } from './pages/AssurancePage.tsx';
import { RemoteRedirect } from './pages/RemotePage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { StatusPage } from './pages/StatusPage.tsx';
import { OperatorAccessPage } from './pages/OperatorAccessPage.tsx';
import { OperatePage } from './pages/OperatePage.tsx';
import { HelpPage } from './pages/cloud/HelpPage.tsx';
import { WorkbenchesPage } from './pages/WorkbenchesPage.tsx';
import { WorkbenchPage } from './pages/WorkbenchPage.tsx';
import { AuthCallbackPage } from './pages/cloud/AuthCallbackPage.tsx';
import { BillingReturnPage } from './pages/cloud/BillingReturnPage.tsx';
import { InvitePage } from './pages/cloud/InvitePage.tsx';
import { SignInPage } from './pages/cloud/SignInPage.tsx';
import { EnterpriseSignInPage, SignedOutPage, StartFailurePage } from './pages/SignInPages.tsx';
import { TeamPage } from './pages/cloud/TeamPage.tsx';
import { TeamsRequiredPrompt } from './components/cloud/UpgradeToTeams.tsx';
import { TeamsOnly } from './pages/TeamsOnlyPage.tsx';
import { isCloud } from './edition.ts';
import { AccountDeletionPage } from './pages/cloud/AccountDeletionPage.tsx';
import { PartBoundary } from './components/ui/PartBoundary.tsx';
import { UsageConsent } from './components/cloud/UsageConsent.tsx';
import { useDateTimePrefs, useLoadDateTimePrefs } from './dateTimePrefs.ts';

/** Enterprise-only views: on Mojo Up AI Cloud their address goes to the fleet instead of a dead page. */
const enterprise = (page: ReactNode) => (isCloud() ? <Navigate to="/workbenches" replace /> : page);

/** The person's date and time choices, loaded once signed in. */
function DateTimePrefsLoader() {
  useLoadDateTimePrefs();
  return null;
}

function Gate() {
  const { ready, error, failure, signedOut, ended, cloud } = useAuth();
  const { pathname } = useLocation();
  // Every date is written the person's way: a change of choice renders the console again.
  useDateTimePrefs();
  // Mojo Up AI Cloud's own sign-in: the provider's way back, and invitation links, work signed out.
  if (isCloud() && pathname === '/auth/callback') return <AuthCallbackPage />;
  // Public: how to delete an account (Facebook Login and app stores link here).
  if (pathname === '/account-deletion') return <AccountDeletionPage />;
  // Public: the installers. A workbench that cannot update itself sends people here, Free and Teams
  // users included, who may have no account; signed in, the page sits in the app as usual.
  if (pathname === '/downloads' && (!ready || signedOut || error)) {
    return (
      <div className="app-content h-full overflow-auto p-4 md:p-6">
        <DownloadsPage />
      </div>
    );
  }
  if (error) return <StartFailurePage discovery={failure === 'discovery'} detail={error} />;
  if (!ready) return <div className="flex h-full items-center justify-center text-sm text-muted">Signing in…</div>;
  if (isCloud() && pathname.startsWith('/invite/')) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  }
  // Signed out: the signed-out page right after signing out (and at its own address, which never
  // signs in by itself); otherwise the edition's sign-in page.
  if (signedOut) {
    if (ended || pathname === SIGNED_OUT_PATH) return <SignedOutPage />;
    return isCloud() ? <SignInPage /> : <EnterpriseSignInPage />;
  }
  // The old remote-control address: the machine's page is the one place now, with the same view.
  if (pathname.startsWith('/remote/')) {
    return (
      <Routes>
        <Route path="/remote/:deviceId" element={<RemoteRedirect />} />
        <Route path="/remote/:deviceId/:view" element={<RemoteRedirect />} />
      </Routes>
    );
  }
  // Keyed by the active organisation: switching loads every view again for the new one.
  return (
    <MoneyProvider key={cloud?.organisation.id}>
    <AppShell>
      <IdleSessionGuard />
      <DateTimePrefsLoader />
      <RenewalDialog />
      {isCloud() ? <TeamsRequiredPrompt /> : null}
      <PartBoundary key={pathname} name="This page" tone="slate">
      <Routes key={cloud?.organisation.id}>
        <Route path="/" element={<HomePage />} />
        <Route path="/workbenches" element={<WorkbenchesPage />} />
        <Route path="/workbenches/:deviceId" element={<WorkbenchPage />} />
        <Route path="/activity" element={<TeamsOnly><ActivityPage /></TeamsOnly>} />
        <Route path="/backups" element={<TeamsOnly><BackupsPage /></TeamsOnly>} />
        <Route path="/updates" element={<TeamsOnly><UpdatesPage /></TeamsOnly>} />
        <Route path="/downloads" element={<DownloadsPage />} />
        <Route path="/policies" element={<TeamsOnly><PoliciesPage /></TeamsOnly>} />
        <Route path="/policies/:policyId" element={<TeamsOnly><PoliciesPage /></TeamsOnly>} />
        <Route path="/assurance" element={<TeamsOnly><AssurancePage /></TeamsOnly>} />
        <Route path="/catalogue" element={<TeamsOnly><CataloguePage /></TeamsOnly>} />
        <Route path="/customisations" element={<TeamsOnly><CustomisationsPage /></TeamsOnly>} />
        <Route path="/apps-and-routines" element={<TeamsOnly><AppsAndRoutinesPage /></TeamsOnly>} />
        <Route path="/apps-and-routines/workflows/:projectId/:workflowId" element={<TeamsOnly><WorkflowPage /></TeamsOnly>} />
        <Route path="/apps-and-routines/workflow-runs/:projectId/:runId" element={<TeamsOnly><WorkflowRunPage /></TeamsOnly>} />
        <Route path="/foundry" element={<TeamsOnly><FoundryPage /></TeamsOnly>} />
        <Route path="/foundry/:tab" element={<TeamsOnly><FoundryPage /></TeamsOnly>} />
        <Route path="/compute" element={<TeamsOnly><ComputePage /></TeamsOnly>} />
        <Route path="/compute/:tab" element={<TeamsOnly><ComputePage /></TeamsOnly>} />
        <Route path="/design-systems" element={<TeamsOnly><DesignSystemsPage /></TeamsOnly>} />
        <Route path="/tools" element={<TeamsOnly><ToolsPage /></TeamsOnly>} />
        <Route path="/tools/:fingerprint" element={<TeamsOnly><ToolsPage /></TeamsOnly>} />
        <Route path="/secrets" element={<TeamsOnly><SecretsPage /></TeamsOnly>} />
        <Route path="/mesh" element={enterprise(<MeshPage />)} />
        <Route path="/nodes" element={<NodesPage />} />
        <Route path="/inbox" element={<TeamsOnly><InboxPage /></TeamsOnly>} />
        <Route path="/live/:deviceId/:key" element={<LivePage />} />
        <Route path="/memory" element={<TeamsOnly><MemoryPage /></TeamsOnly>} />
        <Route path="/budgets" element={<TeamsOnly><BudgetsPage /></TeamsOnly>} />
        <Route path="/costs" element={<TeamsOnly><CostsPage /></TeamsOnly>} />
        <Route path="/audit" element={<TeamsOnly><AuditPage /></TeamsOnly>} />
        <Route path="/labelling" element={<TeamsOnly><LabellingPage /></TeamsOnly>} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/platform-access" element={<OperatorAccessPage />} />
        {isCloud() && <Route path="/operate" element={<OperatePage />} />}
        {isCloud() && <Route path="/help" element={<HelpPage />} />}
        <Route path="/portfolio" element={<TeamsOnly><PortfolioPage /></TeamsOnly>} />
        <Route path="/projects" element={<TeamsOnly><ProjectsPage /></TeamsOnly>} />
        <Route path="/projects/:projectId" element={<TeamsOnly><ProjectPage /></TeamsOnly>} />
        <Route path="/projects/:projectId/teamwork/:cid" element={<TeamsOnly><TeamworkSessionPage /></TeamsOnly>} />
        <Route path="/teams" element={<TeamsOnly><TeamsPage /></TeamsOnly>} />
        <Route path="/teams/:teamId" element={<TeamsOnly><TeamsPage /></TeamsOnly>} />
        <Route path="/teams/:teamId/members/:member" element={<TeamsOnly><TeamsPage /></TeamsOnly>} />
        <Route path="/enrol" element={<EnrolPage />} />
        {isCloud() && <Route path="/team" element={<TeamPage />} />}
        {isCloud() && <Route path="/billing/return" element={<BillingReturnPage />} />}
        <Route path="/billing" element={<BillingPage />} />
        {!isCloud() && <Route path="/quotas" element={<QuotasPage />} />}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:tab" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </PartBoundary>
    </AppShell>
    </MoneyProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
        <UsageConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}
